import type { Tone } from "@amtana/design-system";
import type { Catalogs, PlainKey, Translate, Translation } from "@amtana/i18n/catalog";
import { CSV_TEMPLATE_COLUMNS } from "@amtana/sender-contract";
import type { InvalidReason, Provider, Sender, WarmupStatus } from "@amtana/sender-contract";

// Words for the email accounts list, an account's view, the connect dialog, the
// status labels, the screen titles and the navigation entry, in English and
// Spanish. The form and warmup words are in `sender-form-copy.ts`, bulk edit's
// in `sender-bulk-copy.ts`, errors in `sender-error-copy.ts`.

export type SenderCopyKey = keyof typeof EN;
export type SenderCatalog = typeof EN;

const EN = {
  // Screen titles and the navigation entry (sender.web.ts)
  screenSenders: "Email accounts · {product}",
  screenBulkEdit: "Bulk edit · {product}",
  screenConnect: "Connect account · {product}",
  screenView: "Email account · {product}",
  screenSettings: "Email Account Settings · {product}",
  navEmailAccounts: "Email accounts",

  // Providers and sources
  providerGmail: "Gmail",
  providerOutlook: "Outlook",
  providerSendgrid: "Sendgrid",
  providerSes: "Amazon SES",
  providerSmtp: "SMTP",
  managedMailboxes: "Managed Mailboxes",
  authOAuth: "{provider} (OAuth)",
  authPassword: "SMTP (password)",
  authApiKey: "{provider} (API key)",

  // Warmup status and its messages
  warmupDisabled: "Disabled",
  warmupSettingUp: "Setting up...",
  warmupEnabled: "Enabled",
  warmupFailed: "Failed",
  warmupManaged: "Managed",
  warmupMessageProcessing: "Warmup is being configured. This may take a few minutes.",
  warmupMessageFailed:
    "Warmup setup failed. Please try reconnecting your sender or contact support if the issue persists.",
  warmupMessageManaged:
    "Warmup is managed automatically by the mailbox provider's infrastructure. It is already running for this mailbox and needs no setup.",

  // Why a mailbox is invalid
  invalidAuth:
    "The mailbox credentials were rejected when sending. Reconnect the account or re-enter its password.",
  invalidPermissions:
    "The account is connected without permission to send. Reconnect it and grant the send permission.",
  invalidNoGmail:
    "This Google account has no Gmail, so it can't send email even though it can sign in. Connect it by SMTP with your email provider's details.",
  invalidGeneric:
    "We could not connect to this mailbox. Reconnect the account or re-enter its credentials; if it keeps failing, contact support.",

  // Shared by the list and an account's view
  campaigns: "Campaigns",
  home: "Home",
  emailAccounts: "Email accounts",
  provider: "Provider",
  dailyUsage: "Daily Usage",
  warmup: "Warmup",
  status: "Status",
  tags: "Tags",
  actions: "Actions",
  edit: "Edit",
  delete: "Delete",
  upgrade: "Upgrade",
  connectAccount: "Connect account",
  settingUp: "Setting up...",
  valid: "Valid",
  invalid: "Invalid",
  needsReconnecting: "This mailbox needs reconnecting",

  // The list (senders-screen.tsx)
  listDescription: "Create, add or remove email accounts.",
  connectAccounts: "Connect accounts",
  buyAccounts: "Buy accounts",
  morePurchaseOptions: "More purchase options",
  viewOrders: "View Orders",
  viewDomains: "View Domains",
  viewMailboxes: "View Mailboxes",
  limitReachedTitle: "You have reached the maximum number of email accounts for your plan",
  limitReachedDescription: "Upgrade your plan to connect more email accounts",
  itemsSelected: { one: "1 item selected", other: "{count} items selected" },
  moveToProject: "Move to project",
  movingAccounts: {
    one: "Moving 1 account to the selected project",
    other: "Moving {count} accounts to the selected project",
  },
  couldNotMove: "Could not move the accounts",
  searchAccounts: "Search accounts...",
  refresh: "Refresh",
  syncing: "Syncing...",
  mailboxesUpdated: { one: "1 mailbox updated", other: "{count} mailboxes updated" },
  noChanges: "No changes detected",
  couldNotRefresh: "Could not refresh the accounts",
  filterAccounts: "Filter Email Accounts",
  filters: "Filters",
  filterProvider: "Provider: {provider}",
  filterWarmupOn: "Warmup: Enabled",
  filterWarmupOff: "Warmup: Disabled",
  filterSourceManual: "Source: Self-Connected",
  filterSourceManaged: "Source: Managed Mailboxes",
  filterTag: "Tag: {tag}",
  emptyAccounts: "There are no email accounts right now, what about connecting a new one?",
  columnAccountName: "Account Name",
  sentTodayOf: "{sent} of {total} sent today",
  settingUpTooltip: "This account is being configured. It will be ready in a few minutes.",
  reconnectNeeded: "Reconnect needed",
  actionsFor: "Actions for {email}",
  view: "View",
  deleteTitle: {
    one: "Are you sure you want to delete this email account?",
    other: "Are you sure you want to delete {count} email accounts?",
  },
  cannotUndo: "This action cannot be undone.",
  managedMailboxTitle: "Managed mailbox",
  managedMailboxDescription:
    "This is a managed mailbox. Deleting it will cancel your subscription and stop billing for this mailbox.",
  deleted: {
    one: "Email account deleted successfully",
    other: "{count} email accounts deleted successfully",
  },
  couldNotDelete: "Could not delete the email accounts",

  // The connect dialog (connect-dialog.tsx)
  selectAccountType: "Select Email Account Type",
  providerServersTitle: "{product} uses your provider's servers",
  providerServersDescription:
    "{product} doesn't have its own email servers and uses your email account provider's servers for mailing.",
  or: "OR",
  importMultiple: "Import Multiple Accounts",
  importMultipleHint:
    "Import multiple SMTP email accounts at once using a CSV file. Download the template, fill it with your email credentials, and upload it back.",
  downloadTemplate: "Download Template",
  templateDownloaded: "CSV template downloaded successfully",
  importCsv: "Import CSV",
  importing: "Importing...",
  selectValidCsv: "Please select a valid CSV file",
  importCompleted: "Import completed! {created} accounts created, {failed} failed",
  couldNotImport: "Could not import the CSV file",

  // An account's view (sender-view-screen.tsx)
  editConfiguration: "Edit configuration",
  reconnect: "Reconnect",
  inboxConnectionFailed:
    "Its inbox connection failed (invalid credentials), so we can't read new replies for this mailbox until you reconnect it.",
  authentication: "Authentication",
  warmupStatistics: "Warmup Statistics",
  noStatsTitle: "No Warmup Statistics Available",
  noStatsDescription: "This sender has never had warmup enabled. Enable warmup to start tracking statistics.",
  enableWarmup: "Enable Warmup",
  totalSent: "Total Sent",
  inboxCount: "Inbox Count",
  warmupReceived: "Warmup Received",
  spamCount: "Spam Count",
  sentOverTime: "Sent Emails Over Time",
  sentPerDayChart: "Warmup emails sent per day",
  sentUnit: "sent",
  activityBreakdown: "Activity Breakdown",
  warmupActivity: "Warmup activity",
  legendSent: "Sent Emails {count}",
  legendReplies: "Replies {count}",
  legendSavedFromSpam: "Saved from Spam {count}",
  dailyStatistics: "Daily Statistics",
  dailyStatisticsLabel: "Daily warmup statistics",
  columnDate: "Date",
  columnSent: "Sent",
  columnReplies: "Replies",
  columnSavedFromSpam: "Saved from Spam",
} as const;

const ES: Translation<SenderCatalog> = {
  screenSenders: "Cuentas de correo · {product}",
  screenBulkEdit: "Edición masiva · {product}",
  screenConnect: "Conectar cuenta · {product}",
  screenView: "Cuenta de correo · {product}",
  screenSettings: "Configuración de la cuenta de correo · {product}",
  navEmailAccounts: "Cuentas de correo",

  providerGmail: "Gmail",
  providerOutlook: "Outlook",
  providerSendgrid: "Sendgrid",
  providerSes: "Amazon SES",
  providerSmtp: "SMTP",
  managedMailboxes: "Buzones administrados",
  authOAuth: "{provider} (OAuth)",
  authPassword: "SMTP (contraseña)",
  authApiKey: "{provider} (clave de API)",

  warmupDisabled: "Desactivado",
  warmupSettingUp: "Configurando...",
  warmupEnabled: "Activado",
  warmupFailed: "Con error",
  warmupManaged: "Administrado",
  warmupMessageProcessing: "Se está configurando el calentamiento. Puede tardar unos minutos.",
  warmupMessageFailed:
    "No se pudo configurar el calentamiento. Vuelve a conectar tu cuenta o contacta a soporte si el problema continúa.",
  warmupMessageManaged:
    "El proveedor del buzón administra el calentamiento de forma automática. Ya está en marcha para este buzón y no necesita configuración.",

  invalidAuth:
    "Se rechazaron las credenciales del buzón al enviar. Vuelve a conectar la cuenta o ingresa de nuevo su contraseña.",
  invalidPermissions:
    "La cuenta está conectada sin permiso para enviar. Vuelve a conectarla y concede el permiso de envío.",
  invalidNoGmail:
    "Esta cuenta de Google no tiene Gmail, así que no puede enviar correos aunque pueda iniciar sesión. Conéctala por SMTP con los datos de tu proveedor de correo.",
  invalidGeneric:
    "No pudimos conectarnos a este buzón. Vuelve a conectar la cuenta o ingresa de nuevo sus credenciales. Si sigue fallando, contacta a soporte.",

  campaigns: "Campañas",
  home: "Inicio",
  emailAccounts: "Cuentas de correo",
  provider: "Proveedor",
  dailyUsage: "Uso diario",
  warmup: "Calentamiento",
  status: "Estado",
  tags: "Etiquetas",
  actions: "Acciones",
  edit: "Editar",
  delete: "Eliminar",
  upgrade: "Mejorar plan",
  connectAccount: "Conectar cuenta",
  settingUp: "Configurando...",
  valid: "Válida",
  invalid: "No válida",
  needsReconnecting: "Este buzón necesita volver a conectarse",

  listDescription: "Crea, agrega o quita cuentas de correo.",
  connectAccounts: "Conectar cuentas",
  buyAccounts: "Comprar cuentas",
  morePurchaseOptions: "Más opciones de compra",
  viewOrders: "Ver pedidos",
  viewDomains: "Ver dominios",
  viewMailboxes: "Ver buzones",
  limitReachedTitle: "Alcanzaste el máximo de cuentas de correo de tu plan",
  limitReachedDescription: "Mejora tu plan para conectar más cuentas de correo",
  itemsSelected: { one: "1 elemento seleccionado", other: "{count} elementos seleccionados" },
  moveToProject: "Mover a proyecto",
  movingAccounts: {
    one: "Moviendo 1 cuenta al proyecto seleccionado",
    other: "Moviendo {count} cuentas al proyecto seleccionado",
  },
  couldNotMove: "No se pudieron mover las cuentas",
  searchAccounts: "Buscar cuentas...",
  refresh: "Actualizar",
  syncing: "Sincronizando...",
  mailboxesUpdated: { one: "1 buzón actualizado", other: "{count} buzones actualizados" },
  noChanges: "No se detectaron cambios",
  couldNotRefresh: "No se pudieron actualizar las cuentas",
  filterAccounts: "Filtrar cuentas de correo",
  filters: "Filtros",
  filterProvider: "Proveedor: {provider}",
  filterWarmupOn: "Calentamiento: activado",
  filterWarmupOff: "Calentamiento: desactivado",
  filterSourceManual: "Origen: conectada por ti",
  filterSourceManaged: "Origen: buzones administrados",
  filterTag: "Etiqueta: {tag}",
  emptyAccounts: "Todavía no hay cuentas de correo. ¿Quieres conectar una?",
  columnAccountName: "Nombre de la cuenta",
  sentTodayOf: "{sent} de {total} enviados hoy",
  settingUpTooltip: "Esta cuenta se está configurando. Estará lista en unos minutos.",
  reconnectNeeded: "Hay que volver a conectar",
  actionsFor: "Acciones para {email}",
  view: "Ver",
  deleteTitle: {
    one: "¿Seguro que quieres eliminar esta cuenta de correo?",
    other: "¿Seguro que quieres eliminar {count} cuentas de correo?",
  },
  cannotUndo: "Esta acción no se puede deshacer.",
  managedMailboxTitle: "Buzón administrado",
  managedMailboxDescription:
    "Este es un buzón administrado. Al eliminarlo se cancela tu suscripción y se deja de cobrar por él.",
  deleted: {
    one: "Cuenta de correo eliminada",
    other: "{count} cuentas de correo eliminadas",
  },
  couldNotDelete: "No se pudieron eliminar las cuentas de correo",

  selectAccountType: "Elige el tipo de cuenta de correo",
  providerServersTitle: "{product} usa los servidores de tu proveedor",
  providerServersDescription:
    "{product} no tiene servidores de correo propios. Envía con los servidores del proveedor de tu cuenta de correo.",
  or: "O",
  importMultiple: "Importar varias cuentas",
  importMultipleHint:
    "Importa varias cuentas SMTP a la vez con un archivo CSV. Descarga la plantilla, complétala con tus credenciales y vuelve a subirla.",
  downloadTemplate: "Descargar plantilla",
  templateDownloaded: "Plantilla CSV descargada",
  importCsv: "Importar CSV",
  importing: "Importando...",
  selectValidCsv: "Selecciona un archivo CSV válido",
  importCompleted: "¡Importación completada! {created} cuentas creadas, {failed} con error",
  couldNotImport: "No se pudo importar el archivo CSV",

  editConfiguration: "Editar configuración",
  reconnect: "Volver a conectar",
  inboxConnectionFailed:
    "Falló la conexión de su bandeja de entrada (credenciales no válidas), así que no podemos leer respuestas nuevas de este buzón hasta que lo vuelvas a conectar.",
  authentication: "Autenticación",
  warmupStatistics: "Estadísticas de calentamiento",
  noStatsTitle: "No hay estadísticas de calentamiento",
  noStatsDescription:
    "Esta cuenta nunca ha tenido el calentamiento activado. Actívalo para empezar a ver estadísticas.",
  enableWarmup: "Activar calentamiento",
  totalSent: "Total enviados",
  inboxCount: "En bandeja de entrada",
  warmupReceived: "Recibidos por calentamiento",
  spamCount: "En spam",
  sentOverTime: "Correos enviados en el tiempo",
  sentPerDayChart: "Correos de calentamiento enviados por día",
  sentUnit: "enviados",
  activityBreakdown: "Desglose de actividad",
  warmupActivity: "Actividad de calentamiento",
  legendSent: "Correos enviados {count}",
  legendReplies: "Respuestas {count}",
  legendSavedFromSpam: "Rescatados del spam {count}",
  dailyStatistics: "Estadísticas diarias",
  dailyStatisticsLabel: "Estadísticas diarias de calentamiento",
  columnDate: "Fecha",
  columnSent: "Enviados",
  columnReplies: "Respuestas",
  columnSavedFromSpam: "Rescatados del spam",
};

export const SENDER_CATALOGS: Catalogs<SenderCatalog> = { en: EN, es: ES };

export const PROVIDER_LABEL: Readonly<Record<Provider, PlainKey<SenderCatalog>>> = {
  gmail: "providerGmail",
  outlook: "providerOutlook",
  sendgrid: "providerSendgrid",
  ses: "providerSes",
  smtp: "providerSmtp",
};

export const WARMUP_STATUS: Readonly<Record<WarmupStatus, { label: PlainKey<SenderCatalog>; tone: Tone }>> = {
  off: { label: "warmupDisabled", tone: "neutral" },
  processing: { label: "warmupSettingUp", tone: "info" },
  active: { label: "warmupEnabled", tone: "success" },
  failed: { label: "warmupFailed", tone: "danger" },
  managed: { label: "warmupManaged", tone: "success" },
};

export const WARMUP_STATUS_MESSAGE: Partial<Record<WarmupStatus, PlainKey<SenderCatalog>>> = {
  processing: "warmupMessageProcessing",
  failed: "warmupMessageFailed",
  managed: "warmupMessageManaged",
};

export const INVALID_REASON: Readonly<Record<InvalidReason, PlainKey<SenderCatalog>>> = {
  auth: "invalidAuth",
  permissions: "invalidPermissions",
  "no-gmail": "invalidNoGmail",
  generic: "invalidGeneric",
};

export function authLabel(sender: Sender, t: Translate<SenderCatalog>): string {
  if (sender.source === "managed-mailboxes") return t("managedMailboxes");
  if (sender.provider === "gmail" || sender.provider === "outlook")
    return t("authOAuth", { provider: t(PROVIDER_LABEL[sender.provider]) });
  if (sender.provider === "smtp") return t("authPassword");
  return t("authApiKey", { provider: t(PROVIDER_LABEL[sender.provider]) });
}

/** The bulk-import template: the header and one example row. */
export function csvTemplate(): string {
  const example = [
    "John Doe",
    "john@yourdomain.com",
    "smtp.yourdomain.com",
    "465",
    "john@yourdomain.com",
    "your-password",
    "imap.yourdomain.com",
    "993",
    "30",
    "60",
  ];
  return `${CSV_TEMPLATE_COLUMNS.join(",")}\n${example.join(",")}\n`;
}
